/**
 * Second chance — Eva re-attempts each word she missed on first pass,
 * one at a time, with her wrong attempt shown under a soft warm wash.
 * Implements spec §4.4.
 *
 * Per-word lifecycle (three sub-states):
 *
 *   1. **input** — her first-pass attempt is shown washed, audio plays,
 *      she types into a fresh input and taps Done.
 *      - Correct → 2 (correct beat) → next word
 *      - Wrong   → 3 (show-correct beat) → next word
 *
 *   2. **correct** — big green checkmark + a varied praise line +
 *      Eva's typed word in green so she can see exactly what she got
 *      right. Plays the sticker-reveal chime + speaks the praise line.
 *      Auto-advances after 1.8s.
 *      (Earlier "Nice one!" text-only beat was too quiet — Eva's
 *      live QA showed she couldn't always tell if she got it right
 *      or wrong, so the visual + audio confirmation is now explicit.)
 *
 *   3. **show-correct** — "Almost!" header makes it explicit this is
 *      a correction, followed by "The word is" and the correct
 *      spelling. Eva taps "Got it" to advance. Spec §10's "~2–3
 *      seconds" default proved too fast in live QA — a 6yo needs
 *      reading time *and* a moment to actually understand where she
 *      went wrong, so we let her pace this herself. A "Say it again"
 *      tap is also available. **No third try.**
 *
 * 3x-write applies to *any* first-pass-missed word regardless of the
 * second-chance result (spec §4.5) — so the missed-list for both
 * screens is derived the same way from `missedFromFirstPass`.
 *
 * After the last missed word: navigate to the threeXWrite intro
 * (Pumpkin-led "what's next" beat), which then leads into the 3x-write
 * screen — softens the abrupt handoff for a 6yo.
 *
 * Pumpkin is not visible (spec §7, task-focus).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";

import { Button } from "@/components/Button";
import { useNavigation } from "@/context/NavigationContext";
import { useSession } from "@/context/SessionContext";
import { playGentleChime, playStickerReveal } from "@/lib/sounds";
import { cancelSpeech, speak } from "@/lib/speech";
import { attemptMatches, missedFromFirstPass } from "@/lib/spellingCompare";
import "./SecondChanceScreen.css";

const CORRECT_BEAT_MS = 1800;

/**
 * Short praise lines for the correct beat. Picked at random per
 * correct answer so the celebration doesn't feel scripted across a
 * session with several misses. All five are short enough to land
 * inside the 1.8s beat window when spoken by Google Cloud TTS.
 */
const PRAISE_LINES = ["Brilliant!", "Yes!", "Well done!", "Spot on!", "Got it!"];

type SubState =
  | { kind: "input" }
  | { kind: "correct"; praise: string; typed: string }
  | { kind: "show-correct" };

export function SecondChanceScreen() {
  const { navigate } = useNavigation();
  const { session, recordSecondChanceAttempt } = useSession();
  // Missed list is stable per session — derive once.
  const missed = useMemo(() => {
    if (!session) return [];
    return missedFromFirstPass(session.words, session.firstPassAttempts);
  }, [session]);
  // Resume support (spec §5.3): start at the first missed word that
  // doesn't yet have a recorded second-chance attempt. Attempts get
  // recorded for both correct and wrong tries, so this naturally picks
  // up after the last completed beat.
  const [index, setIndex] = useState(() => {
    if (!session || missed.length === 0) return 0;
    const completed = missed.filter(
      (w) => session.secondChanceAttempts[w.id] !== undefined,
    ).length;
    return Math.min(completed, Math.max(missed.length - 1, 0));
  });
  const [input, setInput] = useState("");
  const [sub, setSub] = useState<SubState>({ kind: "input" });
  const inputRef = useRef<HTMLInputElement>(null);
  // Track the active beat-timer so a fast unmount can cancel it (and a
  // double-fire on StrictMode in dev doesn't queue two advances).
  const beatTimerRef = useRef<number | null>(null);

  // Bounce home (no session) or skip ahead (no misses — e.g. dev landed
  // here directly with 10/10 first pass). The Results screen normally
  // sends 10/10 straight to celebration, so the no-miss branch is defensive.
  // These guards stay reactive — they only fire on genuinely invalid state.
  useEffect(() => {
    if (!session) {
      navigate("home");
      return;
    }
    if (missed.length === 0) {
      navigate("celebration");
    }
  }, [session, missed, navigate]);

  // Resume edge case (spec §5.3): if she'd already finished every
  // second-chance attempt before backgrounding the app (i.e. completed
  // == missed.length), we'd otherwise mount with index === missed.length
  // and crash on a null `word`. Forward to the 3x-write intro instead.
  //
  // This MUST be a mount-only check, not reactive to `session`. Recording
  // the *final* word's attempt updates `session.secondChanceAttempts`,
  // which would otherwise re-fire this effect with completed == missed.length
  // and navigate away *immediately* — skipping the last word's feedback
  // beat (the correct-beat timer or the "Got it" tap). That was the
  // last-word "no feedback" glitch. The ref trips only once session is
  // present, so a late-loading session still gets checked exactly once.
  const resumeCheckedRef = useRef(false);
  useEffect(() => {
    if (resumeCheckedRef.current || !session || missed.length === 0) return;
    resumeCheckedRef.current = true;
    const completed = missed.filter(
      (w) => session.secondChanceAttempts[w.id] !== undefined,
    ).length;
    if (completed >= missed.length) {
      navigate("phaseIntro:threeXWrite");
    }
  }, [session, missed, navigate]);

  const word = missed[index];
  const wordId = word?.id;
  const firstPassAttempt =
    session && word ? (session.firstPassAttempts[word.id] ?? "") : "";

  // Speak each word on arrival, plus once when we enter show-correct.
  // The show-correct effect is handled below; this one fires on word change.
  useEffect(() => {
    if (!session || !word) return;
    speak(word.spelling, session.settings);
    return () => cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordId]);

  // Refocus the input on each fresh word.
  useEffect(() => {
    if (sub.kind === "input") inputRef.current?.focus();
  }, [index, sub.kind]);

  // Clear any in-flight beat timer on unmount so we don't navigate post-leave.
  useEffect(() => {
    return () => {
      if (beatTimerRef.current !== null) {
        window.clearTimeout(beatTimerRef.current);
        beatTimerRef.current = null;
      }
    };
  }, []);

  const advance = useCallback(() => {
    beatTimerRef.current = null;
    setInput("");
    setSub({ kind: "input" });
    if (index === missed.length - 1) {
      navigate("phaseIntro:threeXWrite");
    } else {
      setIndex((i) => i + 1);
    }
  }, [index, missed.length, navigate]);

  const handleSayAgain = useCallback(() => {
    if (!session || !word) return;
    speak(word.spelling, session.settings);
  }, [session, word]);

  const handleHearSentence = useCallback(() => {
    if (!session || !word || word.exampleSentence === null) return;
    speak(word.exampleSentence, session.settings);
  }, [session, word]);

  const handleDone = useCallback(() => {
    if (!session || !word || sub.kind !== "input") return;
    cancelSpeech();
    recordSecondChanceAttempt(word.id, input);
    if (attemptMatches(input, word.spelling)) {
      // Pick the praise line at this exact moment so it's stable
      // across the 1.8s beat (state-stored, not recomputed each
      // render) and so different correct answers in a row don't
      // all get the same line.
      const praise = PRAISE_LINES[Math.floor(Math.random() * PRAISE_LINES.length)];
      setSub({ kind: "correct", praise, typed: input });
      // Chime + spoken praise — the audio confirmation Eva was
      // missing from the old text-only "Nice one!" beat. Chime
      // fires immediately; TTS speech follows naturally after.
      void playStickerReveal();
      speak(praise, session.settings);
      beatTimerRef.current = window.setTimeout(advance, CORRECT_BEAT_MS);
    } else {
      setSub({ kind: "show-correct" });
      // Soft neutral cue (design.md §8 "very neutral low chime") before
      // the correct spelling speaks — marks the moment without using a
      // failure-coded sound. Plays *before* speak() so the chime lands
      // first and the spoken word lands fractionally later.
      void playGentleChime();
      // Speak the correct spelling once on entry, per spec §4.4 "the
      // correct spelling appears... with the word spoken once". Eva
      // controls when to advance via the "Got it" tap below — no timer.
      speak(word.spelling, session.settings);
    }
  }, [session, word, sub.kind, recordSecondChanceAttempt, input, advance]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    handleDone();
  };

  if (!session || !word) return null;

  return (
    <main className="second-chance">
      <div
        className="second-chance__progress"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={missed.length}
        aria-valuenow={index + 1}
        aria-label={`Word ${index + 1} of ${missed.length}`}
      >
        {missed.map((w, i) => (
          <span
            key={w.id}
            className={
              "second-chance__dot" +
              (i < index ? " is-done" : "") +
              (i === index ? " is-current" : "")
            }
            aria-hidden="true"
          />
        ))}
      </div>

      {sub.kind === "input" && (
        <form className="second-chance__form" onSubmit={handleSubmit}>
          <p className="second-chance__lede">Let&rsquo;s try this one again.</p>

          {firstPassAttempt !== "" && (
            <div className="second-chance__prior" aria-label="Your last try">
              <span className="second-chance__prior-label">You wrote</span>
              <span className="second-chance__prior-attempt">
                {firstPassAttempt}
              </span>
            </div>
          )}

          <div className="second-chance__audio-row">
            <button
              type="button"
              className="second-chance__audio-btn"
              onClick={handleSayAgain}
              aria-label="Hear the word again"
            >
              <span aria-hidden="true">🔊</span> Say it again
            </button>
            {word.exampleSentence !== null && (
              <button
                type="button"
                className="second-chance__audio-btn"
                onClick={handleHearSentence}
                aria-label="Hear a sentence"
              >
                <span aria-hidden="true">♪</span> Hear a sentence
              </button>
            )}
          </div>

          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="second-chance__input"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            inputMode="text"
            enterKeyHint="done"
            aria-label="Type the word here"
          />

          <Button type="submit" size="large">
            Done
          </Button>
        </form>
      )}

      {sub.kind === "correct" && (
        <div className="second-chance__beat second-chance__beat--correct">
          <CheckmarkBadge />
          <p className="second-chance__correct-praise">{sub.praise}</p>
          {sub.typed !== "" && (
            <p className="second-chance__correct-word">{sub.typed}</p>
          )}
        </div>
      )}

      {sub.kind === "show-correct" && (
        <div className="second-chance__beat second-chance__beat--show-correct">
          <p className="second-chance__almost">Almost!</p>
          <p className="second-chance__beat-label">The word is</p>
          <p className="second-chance__beat-word">{word.spelling}</p>
          <button
            type="button"
            className="second-chance__audio-btn"
            onClick={handleSayAgain}
            aria-label="Hear the word again"
          >
            <span aria-hidden="true">🔊</span> Say it again
          </button>
          <Button size="large" onClick={advance}>
            Got it
          </Button>
        </div>
      )}
    </main>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────

/**
 * Big green tick inside a soft circle — the visual confirmation Eva
 * was missing. Sized to dominate the beat (~120px on phone) and
 * animated to scale-in on mount via CSS. Inline SVG so it inherits
 * the page's --correct token for the circle fill and --bg-soft for
 * the stroke contrast.
 */
function CheckmarkBadge() {
  return (
    <svg
      className="second-chance__check"
      viewBox="0 0 100 100"
      role="img"
      aria-label="Correct"
    >
      <circle cx="50" cy="50" r="46" fill="var(--correct)" />
      <path
        d="M28 52 L44 68 L72 36"
        stroke="var(--bg-soft)"
        strokeWidth="10"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
